import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { useGetUploadFileParam } from 'api-hooks/upload/upload.mutation';
import Plus from 'baseui/icon/plus';
import { Spinner } from 'baseui/spinner';
import { ResizeImage } from 'common/helpers/image';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import {
  BorderContainer,
  MainDropzoneContainer,
  TextContainer,
} from './styles';
import { onDrop } from './utils';
import Text from '../text';

export interface FilePickerOptions {
  id?: string;
  name?: string;
  disabled?: boolean;
  onPicked: (files: { filename: string; fileUrl: string }[]) => void;
  accept?: string;
  isError?: boolean;
  resizeImage?: boolean;
  resizeWidth?: number;
  resizeHeight?: number;
  isMultiple?: boolean;
  fileType: UploadFileTypeEnum;
}

export const ACCEPTED_FILE =
  'application/pdf,image/*,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessing';

export default function MainFilePicker(props: FilePickerOptions) {
  const {
    onPicked,
    disabled,
    isError,
    resizeImage,
    resizeWidth,
    resizeHeight,
    isMultiple,
    fileType,
  } = props;
  const [isUploading, setIsUploading] = React.useState(false);
  const { mutateAsync: uploadFileParam } = useGetUploadFileParam();
  const { t } = useTranslation();

  const ResizeImageFunc = React.useCallback(
    async (file, type) => {
      const result: File = (await ResizeImage({
        imageToResize: file,
        width: resizeWidth!,
        height: resizeHeight!,
        type,
      })) as File;
      return result;
    },
    [resizeHeight, resizeWidth],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files) =>
      onDrop({
        acceptedFiles: files,
        onPicked,
        ResizeImageFunc,
        setIsUploading,
        uploadFileParam,
        resizeImage,
        isMultiple,
        fileType,
      }),
  });

  const rootProps = getRootProps({
    refKey: 'innerRef',
  });

  if (disabled) {
    return null;
  }

  return (
    <MainDropzoneContainer $error={isError} {...rootProps}>
      <BorderContainer>
        {isUploading ? (
          <>
            <Spinner $size={30} $color="primary" />
          </>
        ) : (
          <>
            <TextContainer>
              <Plus />
              <Text variant="LabelMedium" $style={{ marginLeft: '4px' }}>
                {t('common:upload_file')}
              </Text>
            </TextContainer>
            <input
              {...getInputProps()}
              id={props.id}
              name={props.name}
              accept={ACCEPTED_FILE}
              disabled={disabled}
            />
          </>
        )}
      </BorderContainer>
    </MainDropzoneContainer>
  );
}
